import { LoadingStageModel, ValueModel } from '@kts-front/models';
import { BaseResponse, Nullable } from '@kts-front/types';
import { action, computed, IReactionDisposer, makeObservable } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { ClientEntity } from 'entities/Client';
import { MeetingEntity, MeetingEntityApi, meetingSchema } from 'entities/Meeting';
import { MeetingCreateEntityApi } from 'entities/Meeting/MeetingCreate';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { PopupStore } from 'stores/local/shared/PopupStore';
import { ProjectTimeSlotStore } from 'stores/local/shared/ProjectTimeSlotStore/ProjectTimeSlotStore';
import { getSubAgentOptions } from 'stores/local/shared/SubAgentStore';
import { FieldSelectStore } from 'stores/local/shared/form/FieldSelectStore';
import { isCancelledRequest } from 'utils/isCancelledRequest';

import { MeetingFields } from '../AppConfigStore';
import { NotificationsStore } from '../NotificationsStore';

type Params = {
  notificationStore: NotificationsStore;
  optionalFields: Set<MeetingFields>;
  shownFields: Set<MeetingFields>;
};

export class MeetingUpdateDrawerModal extends PopupStore<number> {
  private readonly _notificationStore: NotificationsStore;

  private readonly _loadMeetingRequest = (id: number) =>
    apiStore.createRequest({
      url: ENDPOINTS.meeting.get(id),
    });

  readonly loadingStage = new LoadingStageModel();

  readonly clientModel = new ValueModel<Nullable<ClientEntity>>(null);

  readonly updateLoadingStage = new LoadingStageModel();

  readonly meetingModel = new ValueModel<Nullable<MeetingEntity>>(null);

  private readonly _updateMeetingRequest = (id: number) =>
    apiStore.createRequest({
      url: ENDPOINTS.meeting.update(id),
      method: 'PUT',
    });

  projectTimeSlot: ProjectTimeSlotStore;

  private readonly _subAgent: FieldSelectStore;

  private readonly _disposers: IReactionDisposer[];

  constructor({ notificationStore, optionalFields, shownFields }: Params) {
    super();

    this._notificationStore = notificationStore;

    this.projectTimeSlot = new ProjectTimeSlotStore({
      isProjectSelectRequired: !optionalFields.has(MeetingFields.projectId),
      isProjectSelectShown: shownFields.has(MeetingFields.projectId),
      isSlotSelectRequired: !optionalFields.has(MeetingFields.slotId),
      isSlotSelectShown: shownFields.has(MeetingFields.slotId),
    });

    this._subAgent = new FieldSelectStore({
      getOptions: getSubAgentOptions,
      required: !optionalFields.has(MeetingFields.subAgent),
      shown: shownFields.has(MeetingFields.subAgent),
    });

    makeObservable(this, {
      subAgent: computed,
      isError: computed,
      isTouched: computed,
      isProjectSelected: computed,
      resetFields: action.bound,
      validate: action.bound,
      updateMeeting: action.bound,
      openWithMeeting: action.bound,
      fetchMeeting: action.bound,
    });

    this._disposers = [];
  }

  get isProjectSelected(): boolean {
    return this.projectTimeSlot.projects.value.length > 0;
  }

  get subAgent(): FieldSelectStore {
    return this._subAgent;
  }

  get isError(): boolean {
    return this.projectTimeSlot.isError;
  }

  get isTouched(): boolean {
    return this.projectTimeSlot.isTouched;
  }

  async updateMeeting() {
    if (this.isError || !this.entity) {
      return;
    }

    this.updateLoadingStage.loading();

    const requestPayload = {
      meeting_id: this.entity,
      client_id: this.clientModel.value?.id,
      lead_id: this.meetingModel.value?.lead?.id ?? null,
      project_id: this.projectTimeSlot.projects.value[0]?.key
        ? Number(this.projectTimeSlot.projects.value[0].key)
        : null,
      is_offline: this.projectTimeSlot.isOffline.value[0]?.key === 'true',
      slot_id: this.projectTimeSlot.slot.value[0]?.key ? Number(this.projectTimeSlot.slot.value[0].key) : null,
      sub_agent_id: this._subAgent.value[0]?.key ? Number(this._subAgent.value[0].key) : null,
    };

    const response = await this._updateMeetingRequest(this.entity).call<MeetingCreateEntityApi>({
      data: requestPayload,
    });

    if (response.isError) {
      if (isCancelledRequest(response)) {
        this.updateLoadingStage.reset();

        return;
      }

      this.updateLoadingStage.error();
      this._notificationStore.showErrorByResponse(response);

      logApiErrWithSentry({
        endpoint: ENDPOINTS.meeting.update(this.entity),
        method: 'PUT',
        requestPayload,
        response,
      });

      return { Error: true };
    }

    this.updateLoadingStage.success();
    this._notificationStore.showSuccess('Встреча изменена');
    this.close();
  }

  async fetchMeeting(id: number): Promise<void> {
    this.loadingStage.loading();
    const response = await this._loadMeeting(id);

    if (response.isError) {
      return;
    }

    if (response.data) {
      await this._parseMeeting(response.data);
    }

    this.loadingStage.success();
  }

  openWithMeeting(id: number): void {
    super.open(id);
    this.fetchMeeting(id);
  }

  validate(): boolean {
    this.projectTimeSlot.validate();

    return this.isError;
  }

  resetFields() {
    this.projectTimeSlot.resetFields();
    this._subAgent.resetValue();
    this.meetingModel.change(null);
    this.clientModel.change(null);
  }

  close(): void {
    super.close();
    this.resetFields();
  }

  destroy() {
    this._disposers.forEach((d) => d());
  }

  private async _loadMeeting(id: number): Promise<BaseResponse<MeetingEntity>> {
    const response = await this._loadMeetingRequest(id).call<MeetingEntityApi>();

    if (response.isError) {
      if (isCancelledRequest(response)) {
        this.loadingStage.reset();

        return { isError: true };
      }

      this.loadingStage.error();
      this._notificationStore.showErrorByResponse(response);

      logApiErrWithSentry({
        endpoint: ENDPOINTS.meeting.get(id),
        method: 'GET',
        requestPayload: { meeting_id: id },
        response,
      });

      return { isError: true };
    }

    const parsed = meetingSchema.safeParse(response.data);

    if (parsed.error || !parsed.data) {
      this.loadingStage.error();
      this._notificationStore.showErrorByResponse(response);

      return { isError: true };
    }

    return {
      isError: false,
      data: parsed.data,
    };
  }

  private async _parseMeeting(data: MeetingEntity) {
    this.meetingModel.change(data);

    if (data.client) {
      this.clientModel.change(data.client);
    }

    // Инициализируем субагента из lead
    if (data.lead?.subAgent && this._subAgent.shown) {
      await this._subAgent.load();

      const subAgentKey = data.lead.subAgent.id.toString();

      this._subAgent.setValueByKey(subAgentKey);
    }

    await this.projectTimeSlot.isOffline.syncValueByKeys([String(data.isOffline)]);

    if (data.project) {
      this.projectTimeSlot.projects.projectId.change(data.project.id);
      await this.projectTimeSlot.projects.load();
    }

    if (data.slot && data.project) {
      const slotDate = new Date(data.slot.startDate);

      this.projectTimeSlot.date.setValue(slotDate, { noTouch: true });
    }
  }
}
